import React from 'react'
import { AuthenticatedUser, Contact, defaultAuthenticatedUser, defaultContact } from 'models'
import axios from 'axios'
import { LOGIN_TYPE, PATHS, SESSION_STORAGE_KEYS } from 'utils/constants'
import axiosInstance from 'api/axiosInstance/axiosInstance'

type Claim = {
  typ: string
  val: string
}

type BootstrapState = {
  authenticatedUser: AuthenticatedUser
  contact: Contact
  isError: boolean
  isLoginB2B: boolean
  isLoginB2C: boolean
  isLoginLocalB2B: boolean
  displayedName: string
  displayedEmail: string
}

const defaultBootstrapState: BootstrapState = {
  authenticatedUser: defaultAuthenticatedUser,
  contact: defaultContact,
  isError: false,
  isLoginB2B: true,
  isLoginB2C: false,
  isLoginLocalB2B: true,
  displayedName: '',
  displayedEmail: '',
}

let bootstrapStateCache: BootstrapState | null = null
let bootstrapStatePromise: Promise<BootstrapState> | null = null

const isLocalB2CSession = (provider: string | undefined) =>
  typeof window !== 'undefined' &&
  window.location.hostname === 'localhost' &&
  process.env.NODE_ENV === 'development' &&
  provider === 'localAad' &&
  sessionStorage.getItem(SESSION_STORAGE_KEYS.LOCAL_AUTH_LOGIN_TYPE) === LOGIN_TYPE.B2C

const resolveBootstrapState = async (): Promise<BootstrapState> => {
  try {
    const userResponse = await axios.get<AuthenticatedUser>(PATHS.userInfo)
    const user = userResponse.data
    const provider = user.clientPrincipal?.identityProvider
    const localB2C = isLocalB2CSession(provider)
    const claims = (user.clientPrincipal?.claims as Array<Claim>) || []

    const displayedName =
      provider === 'localAad'
        ? user.clientPrincipal?.userDetails || '-'
        : claims.find((claim) => claim.typ === 'name')?.val || '-'
    const displayedEmail =
      provider !== 'b2c'
        ? user.clientPrincipal?.userDetails || '-'
        : claims.find((claim) => claim.typ === 'emails')?.val || '-'

    const bootstrapFromUser: BootstrapState = {
      authenticatedUser: user,
      contact: defaultContact,
      isError: false,
      isLoginB2B: provider === 'aad',
      isLoginB2C: provider === 'b2c' || localB2C,
      isLoginLocalB2B: provider === 'localAad' && !localB2C,
      displayedName,
      displayedEmail,
    }

    const resolvedContactId = user.clientPrincipal?.userId?.trim() || ''
    if (!resolvedContactId) {
      return bootstrapFromUser
    }

    try {
      const contactResponse = await axiosInstance.get<Contact>('/contact', {
        params: { id: resolvedContactId },
      })

      return {
        ...bootstrapFromUser,
        contact: contactResponse.data,
      }
    } catch (_contactError) {
      return {
        ...bootstrapFromUser,
        isError: true,
      }
    }
  } catch (_userError) {
    return {
      ...defaultBootstrapState,
      isError: true,
    }
  }
}

const getBootstrapState = () => {
  if (bootstrapStateCache) {
    return Promise.resolve(bootstrapStateCache)
  }

  if (!bootstrapStatePromise) {
    bootstrapStatePromise = resolveBootstrapState()
      .then((resolvedState) => {
        bootstrapStateCache = resolvedState
        return resolvedState
      })
      .finally(() => {
        bootstrapStatePromise = null
      })
  }

  return bootstrapStatePromise
}

const AuthenticatedUserContext = React.createContext({
  authenticatedUser: defaultAuthenticatedUser,
  setAuthenticatedUser: ((_authenticatedUser: AuthenticatedUser) => {}) as React.Dispatch<
    React.SetStateAction<AuthenticatedUser>
  >,
  isLoading: true,
  isError: false,
  isLoginB2B: false,
  isLoginB2C: false,
  isLoginLocalB2B: true,
  contact: defaultContact,
  displayedName: '',
  displayedEmail: '',
})

const AuthenticatedUserProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const [authenticatedUser, setAuthenticatedUser] = React.useState(defaultAuthenticatedUser)
  const [isLoading, setLoading] = React.useState(true)
  const [isError, setError] = React.useState<boolean>(defaultBootstrapState.isError)
  const [isLoginB2B, setIsLoginB2B] = React.useState<boolean>(true)
  const [isLoginB2C, setIsLoginB2C] = React.useState<boolean>(false)
  const [isLoginLocalB2B, setIsLoginLocalB2B] = React.useState<boolean>(true)

  const [contact, setContact] = React.useState<Contact>(defaultContact)

  const [displayedEmail, setDisplayedEmail] = React.useState<string>('')
  const [displayedName, setDisplayedName] = React.useState<string>('')

  React.useEffect(() => {
    let isMounted = true

    getBootstrapState().then((bootstrapState) => {
      if (!isMounted) return

      setAuthenticatedUser(bootstrapState.authenticatedUser)
      setContact(bootstrapState.contact)
      setError(bootstrapState.isError)
      setIsLoginB2B(bootstrapState.isLoginB2B)
      setIsLoginB2C(bootstrapState.isLoginB2C)
      setIsLoginLocalB2B(bootstrapState.isLoginLocalB2B)
      setDisplayedName(bootstrapState.displayedName)
      setDisplayedEmail(bootstrapState.displayedEmail)
      setLoading(false)
    })

    return () => {
      isMounted = false
    }
  }, [])

  const providerValue = React.useMemo(
    () => ({
      isError,
      contact,
      isLoading,
      authenticatedUser,
      isLoginB2B,
      isLoginB2C,
      isLoginLocalB2B,
      setAuthenticatedUser,
      displayedEmail,
      displayedName,
    }),
    [
      authenticatedUser,
      contact,
      isError,
      isLoading,
      isLoginB2B,
      isLoginB2C,
      isLoginLocalB2B,
      displayedEmail,
      displayedName,
    ],
  )

  return (
    <AuthenticatedUserContext.Provider value={providerValue}>
      {children}
    </AuthenticatedUserContext.Provider>
  )
}

const useAuthenticatedUser = () => React.useContext(AuthenticatedUserContext)

export { AuthenticatedUserContext, AuthenticatedUserProvider, useAuthenticatedUser }
